<template>
  <Modal
    fullbelowsm
    :title="modalTitle"
    :primary-button="primaryButtonText"
    secondary-button="Cancel"
    :primary-button-loading="saving || generating"
    :primary-button-disabled="primaryDisabled"
    @primary="onPrimary"
    @secondary="$emit('close')"
  >
    <div class="sm:w-800 px-6 pb-2 h-full my-10">
      <div v-if="!editTool" class="flex gap-x-2 mb-4">
        <PillButton text="Manual" :primary="mode === 'manual'" @click="mode = 'manual'" />
        <PillButton text="AI generation" :primary="mode === 'ai'" @click="mode = 'ai'" />
      </div>

      <div v-if="mode === 'ai' && !editTool" class="flex flex-col gap-y-3">
        <div class="grid gap-3 sm:grid-cols-2">
          <FormSelect
            v-model="generation.ai_provider"
            label="AI provider"
            :options="providerOptions"
            :selectable="(option) => !option.disabled"
            default-reducer
            :clearable="false"
            @update:modelValue="onProviderChange"
          />
          <FormSelect v-model="generation.ai_model" label="AI model" :options="modelOptions" default-reducer :clearable="false" />
        </div>

        <!-- Table selector — only for SQL-capable adapters. Every column of the selected tables, with its type, is given to the AI. -->
        <div v-if="isSqlAdapter">
          <div class="flex items-center justify-between mb-2">
            <div>
              <div class="text-12 font-600">Database tables</div>
              <div class="text-11 text-semi-dark-text">Select the tables the AI should use. Their columns and types are included automatically.</div>
            </div>
            <PillButton text="Load tables" :loading="loadingTables" @click="loadTables" />
          </div>
          <template v-if="availableTables.length">
            <label class="flex items-center gap-x-2 px-3 py-2 cursor-pointer">
              <input type="checkbox" :checked="allTablesSelected" :indeterminate.prop="someTablesSelected" class="flex-shrink-0" @change="toggleAllTables" />
              <span class="text-12 font-600">Select all ({{ availableTables.length }})</span>
            </label>
            <div class="border border-solid border-stroke rounded-8 max-h-44 overflow-y-auto">
              <label v-for="table in availableTables" :key="table" class="flex items-center gap-x-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-t border-stroke first:border-t-0">
                <input type="checkbox" :value="table" v-model="selectedTables" class="flex-shrink-0" @change="errors.tables = ''" />
                <span class="text-12 font-600">{{ table }}</span>
              </label>
            </div>
          </template>
          <div v-else-if="tableLoadError" class="text-12 text-error mt-1">{{ tableLoadError }}</div>
          <div v-else class="text-12 text-semi-dark-text">Click "Load tables" to browse available tables in the connected database</div>
          <div v-if="errors.tables" class="text-12 text-error mt-1">{{ errors.tables }}</div>
          <div v-else-if="selectedTables.length" class="text-11 text-semi-dark-text mt-1">
            {{ selectedTables.length }} table{{ selectedTables.length !== 1 ? 's' : '' }} selected — schema will be included automatically
          </div>
          <div v-else-if="availableTables.length" class="text-11 text-semi-dark-text mt-1">Select at least one table to generate a tool.</div>
        </div>

        <FormTextarea v-model="generation.prompt" label="Generation prompt" :error="errors.prompt" placeholder="Create a tool for listing recent orders by customer." />

        <div v-if="generatedTools.length" ref="generatedResults">
          <div class="flex items-center justify-between mb-2">
            <div>
              <div class="text-12 font-600">Generated {{ generatedTools.length > 1 ? 'tools' : 'tool' }}</div>
              <div class="text-11 text-semi-dark-text">
                {{ generatedTools.length > 1 ? 'Choose the tool to add, review its query, then click Add tool.' : 'Review the query, then click Add tool.' }}
              </div>
            </div>
            <PillButton text="Regenerate" small outlined :loading="generating" @click="generateTools" />
          </div>
          <div class="border border-solid border-stroke rounded-8">
            <div v-for="tool in generatedTools" :key="tool.name" class="border-t border-solid border-stroke first:border-t-0">
              <div class="flex items-center justify-between gap-x-3 p-3">
                <label class="flex items-start gap-x-2 min-w-0" :class="{ 'cursor-pointer': generatedTools.length > 1 }">
                  <input v-if="generatedTools.length > 1" type="radio" :value="tool.name" v-model="selectedToolName" :disabled="tool.valid === false" class="flex-shrink-0 mt-1" />
                  <span class="min-w-0">
                    <span class="block font-700 truncate">{{ tool.name }}</span>
                    <span class="block text-12 text-semi-dark-text truncate">{{ tool.description }}</span>
                    <span v-if="tool.errors?.length" class="block text-12 text-error mt-1">{{ tool.errors.join(', ') }}</span>
                  </span>
                </label>
                <div class="flex gap-x-2 flex-shrink-0">
                  <PillButton :text="expandedTool === tool.name ? 'Hide' : 'Details'" @click="toggleTool(tool)" />
                </div>
              </div>
              <div v-if="expandedTool === tool.name" class="px-3 pb-3 grid gap-3">
                <div>
                  <div class="text-12 font-700 mb-1">SQL query</div>
                  <pre class="text-11 overflow-x-auto whitespace-pre-wrap break-words bg-on-tertiary rounded-8 p-3">{{ tool.implementation?.query || '' }}</pre>
                </div>
                <div>
                  <div class="text-12 font-700 mb-1">Output</div>
                  <pre class="text-11 overflow-x-auto whitespace-pre-wrap break-words bg-on-tertiary rounded-8 p-3">{{ formatJson(tool.output) }}</pre>
                </div>
                <div>
                  <div class="text-12 font-700 mb-1">Parameter bindings</div>
                  <pre class="text-11 overflow-x-auto whitespace-pre-wrap break-words bg-on-tertiary rounded-8 p-3">{{ formatJson(tool.implementation?.param_bindings || {}) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="grid gap-3 sm:grid-cols-2">
        <FormInput v-model="tool.name" label="Tool name" :error="errors.name" placeholder="get_customer" />
        <div class="flex items-center gap-x-2 mt-5">
          <Toggle v-model="tool.enabled" dense />
          <span class="text-13 font-500">Enabled</span>
        </div>
        <FormTextarea v-model="tool.description" label="Description" class="sm:col-span-2" :error="errors.description" />
        <div class="sm:col-span-2">
          <PhraseEditor v-model="query" label="SQL query" multiline :items="variables" placeholder="SELECT * FROM customers WHERE id = {customer_id}" />
          <div v-if="errors.query" class="text-error text-12 mt-1">{{ errors.query }}</div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import uuidv4 from '@/helpers/uuid';
import { apiGetDataAdapterTables, apiPostDataAdapterGenerateTools, apiPostDataAdapterTablesDdl } from '@/helpers/api';

const SQL_ADAPTER_TYPES = new Set(['postgres', 'mysql', 'mssql', 'd1', 'databricks']);
// Filled in by a Data Export block itself, so always optional on a tool.
const EXPORT_SYSTEM_PARAMS = ['since', 'start_date'];

// A {param} or {{param}} placeholder: the name starts with a letter or underscore, so regex
// quantifiers such as '[0-9]{4}' are never parameters, and single-quoted string literals
// (matched first, no captured name) are left alone. Mirrors the studio and runtime.
const SQL_PLACEHOLDER_PATTERN = /'(?:[^']|'')*'|\{\{([A-Za-z_]\w*)\}\}|\{([A-Za-z_]\w*)\}/g;

/**
 * Lists the parameters a SQL query uses.
 * @param {string} query SQL with {param} or {{param}} placeholders.
 * @returns {string[]} Parameter names, in first-use order.
 */
function extractVariables(query) {
  const names = new Set();
  String(query || '').replace(SQL_PLACEHOLDER_PATTERN, (match, doubleBrace, singleBrace) => {
    if (doubleBrace || singleBrace) names.add(doubleBrace || singleBrace);
    return match;
  });
  return [...names];
}

/**
 * Rewrites {{param}} placeholders as {param}, leaving string literals alone.
 * @param {string} query SQL query.
 * @returns {string} The normalised query.
 */
function normalizeSqlQuery(query) {
  return String(query || '').replace(SQL_PLACEHOLDER_PATTERN, (match, doubleBrace) => (doubleBrace ? `{${doubleBrace}}` : match));
}

export default {
  name: 'DataAdapterToolModal',
  props: {
    editTool: Object,
    saving: Boolean,
    connectionType: String,
  },
  emits: ['save', 'close'],
  data() {
    const tool = this.editTool || {};
    return {
      mode: 'manual',
      generating: false,
      expandedTool: '',
      generatedTools: [],
      // The generated tool the primary button adds.
      selectedToolName: '',
      availableTables: [],
      selectedTables: [],
      loadingTables: false,
      tableLoadError: null,
      tool: {
        name: tool.name || '',
        description: tool.description || '',
        enabled: tool.enabled !== false,
      },
      query: tool.implementation?.query || '',
      generation: {
        prompt: '',
        ai_provider: '',
        ai_model: '',
      },
      errors: {},
    };
  },
  computed: {
    ...mapState(['projectData', 'availableAIProviders']),
    ...mapGetters(['getProjectAIProviderOptions']),
    modalTitle() {
      if (this.editTool) return 'Edit tool';
      return this.mode === 'ai' ? 'Generate tool' : 'Create tool';
    },
    /**
     * @returns {string} Generate, then Add tool once tools are generated, or Save for a manual or edited tool.
     */
    primaryButtonText() {
      if (this.reviewingGenerated) return 'Add tool';
      return this.mode === 'ai' && !this.editTool ? 'Generate' : 'Save';
    },
    /**
     * @returns {boolean} Whether generated tools are shown, so the primary action is adding one.
     */
    reviewingGenerated() {
      return this.mode === 'ai' && !this.editTool && this.generatedTools.length > 0;
    },
    /**
     * @returns {object|null} The generated tool the primary button adds.
     */
    selectedGeneratedTool() {
      return this.generatedTools.find((tool) => tool.name === this.selectedToolName) || null;
    },
    /**
     * @returns {boolean} Whether the primary button is disabled: generation needs tables, adding needs a valid chosen tool.
     */
    primaryDisabled() {
      if (this.reviewingGenerated) return !this.selectedGeneratedTool || this.selectedGeneratedTool.valid === false;
      return this.generationBlocked;
    },
    projectId() {
      return this.$route.params.projectId;
    },
    adapterType() {
      return this.$route.params.dataAdapter;
    },
    isSqlAdapter() {
      return SQL_ADAPTER_TYPES.has(this.connectionType);
    },
    /**
     * @returns {boolean} Whether every loaded table is selected.
     */
    allTablesSelected() {
      return this.availableTables.length > 0 && this.selectedTables.length === this.availableTables.length;
    },
    /**
     * @returns {boolean} Whether some, but not all, loaded tables are selected (the Select all box shows a dash).
     */
    someTablesSelected() {
      return this.selectedTables.length > 0 && !this.allTablesSelected;
    },
    /**
     * @returns {boolean} Whether AI generation must wait: SQL adapters need their tables loaded and at least one selected.
     */
    generationBlocked() {
      return this.mode === 'ai' && !this.editTool && this.isSqlAdapter && !this.selectedTables.length;
    },
    providerOptions() {
      return this.getProjectAIProviderOptions();
    },
    modelOptions() {
      return Object.values(this.availableAIProviders?.[this.generation.ai_provider]?.models || {}).map((model) => ({
        label: `<b>${model.name || model.model}</b>${model.cost ? ` - <i class="text-11">Cost: ${model.cost}</i>` : ''}`,
        value: model.model,
      }));
    },
    variables() {
      return Object.keys(this.projectData?.[this.projectId]?.variables || {})
        .map((variable) => ({ id: uuidv4(), value: variable }))
        .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
    },
  },
  created() {
    this.fetchProjectAIProviders({ projectId: this.projectId });
  },
  methods: {
    ...mapActions(['fetchProjectAIProviders']),
    buildParameters(variableNames) {
      // {since} and {start_date} are filled in by a Data Export block, so never required.
      const required = variableNames.filter((name) => !EXPORT_SYSTEM_PARAMS.includes(name));
      return { type: 'object', properties: Object.fromEntries(variableNames.map((name) => [name, { type: 'string', description: name }])), required, additionalProperties: false };
    },
    buildBindings(variableNames) {
      return Object.fromEntries(variableNames.map((name) => [name, { type: 'text', nullable: EXPORT_SYSTEM_PARAMS.includes(name), default: null }]));
    },
    /**
     * The DDL of the selected tables, with every column and its type, given to the AI as context.
     * @returns {Promise<string>} DDL, or '' when no tables are selected.
     */
    async selectedSchemaContext() {
      if (!this.isSqlAdapter || !this.selectedTables.length) return '';
      const ddlResponse = await apiPostDataAdapterTablesDdl({ project_id: this.projectId, adapter_type: this.adapterType, tables: this.selectedTables });
      return ddlResponse.status === 200 && ddlResponse.data?.success ? ddlResponse.data.schema_context || '' : '';
    },
    /**
     * Selects every loaded table, or clears the selection when all are already selected.
     * @returns {void}
     */
    toggleAllTables() {
      this.selectedTables = this.allTablesSelected ? [] : [...this.availableTables];
      this.errors.tables = '';
    },
    formatJson(value) {
      return JSON.stringify(value || {}, null, 2);
    },
    toggleTool(tool) {
      this.expandedTool = this.expandedTool === tool.name ? '' : tool.name;
    },
    onProviderChange() {
      this.generation.ai_model = this.modelOptions[0]?.value || '';
    },
    /**
     * The modal's primary action: add the chosen generated tool, generate, or save.
     * @returns {void}
     */
    onPrimary() {
      if (this.reviewingGenerated) this.saveGeneratedTool(this.selectedGeneratedTool);
      else if (this.mode === 'ai' && !this.editTool) this.generateTools();
      else this.save();
    },
    /**
     * Drops generated tools once the settings they came from change, so the primary action is Generate again.
     * @returns {void}
     */
    clearGenerated() {
      if (!this.generatedTools.length || this.generating) return;
      this.generatedTools = [];
      this.selectedToolName = '';
      this.expandedTool = '';
    },
    async loadTables() {
      this.loadingTables = true;
      this.tableLoadError = null;
      const response = await apiGetDataAdapterTables({ project_id: this.projectId, adapter_type: this.adapterType });
      this.loadingTables = false;
      if (response.status === 200 && response.data?.success) {
        this.availableTables = response.data.tables || [];
        // Keep only selections that still exist after a reload.
        this.selectedTables = this.selectedTables.filter((table) => this.availableTables.includes(table));
        if (!this.availableTables.length) this.tableLoadError = 'No tables found in the connected database';
      } else {
        this.tableLoadError = response.data?.message || 'Failed to load tables';
      }
    },
    async generateTools() {
      this.errors = {};
      if (this.isSqlAdapter && !this.availableTables.length) this.errors.tables = 'Load the tables first, then select the ones the AI should use.';
      else if (this.isSqlAdapter && !this.selectedTables.length) this.errors.tables = 'Select at least one table.';
      if (!this.generation.prompt) this.errors.prompt = 'Describe the tool you want to create';
      if (Object.keys(this.errors).length) return;
      this.generating = true;
      const schema_context = await this.selectedSchemaContext();

      const response = await apiPostDataAdapterGenerateTools({ project_id: this.projectId, adapter_type: this.adapterType, ...this.generation, schema_context });
      this.generating = false;
      if (response.status === 200 && response.data?.success) {
        this.generatedTools = response.data.tools || [];
        this.selectedToolName = (this.generatedTools.find((tool) => tool.valid !== false) || this.generatedTools[0])?.name || '';
        this.expandedTool = this.selectedToolName;
        if (!this.generatedTools.length) this.errors.prompt = 'The AI did not return a tool. Try again, or describe the tool in more detail.';
        this.$nextTick(() => this.$refs.generatedResults?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      } else {
        this.errors.prompt = response.data?.message || 'Failed to generate tools';
      }
    },
    saveGeneratedTool(tool) {
      this.$emit('save', {
        ...tool,
        // Replaced on save by the columns the database reports for the query.
        output: { type: 'DataTable', columns: tool.output?.columns || [] },
        implementation: { ...tool.implementation, query: normalizeSqlQuery(tool.implementation?.query || '') },
      });
    },
    save() {
      this.errors = {};
      if (!/^[a-z][a-z0-9_]{1,62}$/.test(this.tool.name)) this.errors.name = 'Use lowercase letters, numbers, and underscores';
      if (!this.tool.description || this.tool.description.length < 10) this.errors.description = 'Description must be at least 10 characters';
      if (!this.query) this.errors.query = 'SQL query is required';
      if (Object.keys(this.errors).length) return;
      const query = normalizeSqlQuery(this.query);
      const variableNames = extractVariables(query);
      const parameters = this.buildParameters(variableNames);
      const param_bindings = this.buildBindings(variableNames);
      this.$emit('save', {
        ...this.tool,
        parameters,
        implementation: { method: 'sql', query, param_bindings },
        output: this.editTool?.output || { type: 'DataTable', columns: [] },
        sample_args: this.editTool?.test?.sample_args || this.editTool?.sample_args || {},
        tool_id: this.editTool?.id,
      });
    },
  },
  watch: {
    'generation.prompt': 'clearGenerated',
    'generation.ai_provider': 'clearGenerated',
    'generation.ai_model': 'clearGenerated',
    selectedTables: 'clearGenerated',
    providerOptions: {
      handler(options) {
        const firstEnabled = options.find((option) => !option.disabled);
        if (!this.generation.ai_provider && firstEnabled) this.generation.ai_provider = firstEnabled.value;
      },
      immediate: true,
    },
    modelOptions: {
      handler(options) {
        if (!this.generation.ai_model && options.length) this.generation.ai_model = options[0].value;
      },
      immediate: true,
    },
  },
};
</script>
